import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import { captureError } from '../../../../utils/sentryHelpers';

const client = generateClient();

/**
 * Hook for managing file uploads for lectures
 */
export const useFileUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  /**
   * Upload a lecture image using the uploadCompanyImage mutation
   */
  const uploadLectureImage = useCallback(async (file) => {
    if (!file) return null;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Validate file type - validation errors should not be sent to Sentry
      const allowedTypes = ['image/jpeg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        const errorMessage = 'Please select a valid image file (JPG or PNG)';
        setUploadError(errorMessage);
        setIsUploading(false);
        return null;
      }

      // Validate file size (max 5MB) - validation errors should not be sent to Sentry
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        const errorMessage = 'File size must be less than 5MB';
        setUploadError(errorMessage);
        setIsUploading(false);
        return null;
      }

      // Convert file to base64
      const base64String = await fileToBase64(file);

      // Upload using GraphQL mutation
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
        variables: {
          imageBuffer: base64String,
          imageType: "lectureImage",
        },
      });

      // Parse response
      const result = response.data.uploadCompanyImage;
      if (result.includes("error=")) {
        // Backend validation error - set error message but don't throw (don't report to Sentry)
        const errorMessage = result.replace(/{|}|error=/g, '');
        setUploadError(errorMessage);
        setIsUploading(false);
        return null;
      }

      const fileName = result.replace(/{|}|fileName=/g, '');

      setUploadProgress(100);

      return {
        fileName,
        alt: "Lecture Image",
        file: file
      };

    } catch (error) {
      // Only log technical errors to Sentry (network errors, unexpected errors, etc.)
      // Backend validation errors are already handled above and won't reach here
      captureError(error, {
        feature: 'lecture-management',
        operation: 'upload-lecture-image',
        consoleMessage: 'Upload error:',
        context: { fileName: file?.name, fileType: file?.type, fileSize: file?.size }
      });
      const errorMessage = error.message || 'Upload failed. Please try again.';
      setUploadError(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Upload a lecture file (PDF) using Amplify Storage
   */
  const uploadLectureFile = useCallback(async (file, type = 'handout') => {
    if (!file) return null;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);

    try {
      // Validate file type for handouts (PDF only) - validation errors should not be sent to Sentry
      if ((type === 'handout' || type.startsWith('handout_')) && file.type !== 'application/pdf') {
        const errorMessage = 'Only PDF files are allowed for handouts';
        setUploadError(errorMessage);
        setIsUploading(false);
        return null;
      }

      // Validate file size based on type - validation errors should not be sent to Sentry
      const maxSize = type === 'presentation' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        const errorMessage = `File size must be less than ${maxSize / 1024 / 1024}MB`;
        setUploadError(errorMessage);
        setIsUploading(false);
        return null;
      }

      // Upload to S3 with progress tracking
      const result = await uploadData({
        key: file.name,
        data: file,
        options: {
          accessLevel: 'protected',
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const progress = Math.round((transferredBytes / totalBytes) * 100);
              setUploadProgress(progress);
            }
          }
        }
      }).result;

      // Get the identity ID for the S3 path
      const session = await fetchAuthSession();

      return {
        fileName: result.key,
        name: result.key,
        s3Path: `protected/${session.identityId}/${result.key}`,
        file: file
      };

    } catch (error) {
      // Only log technical errors to Sentry, not validation errors
      captureError(error, {
        feature: 'lecture-management',
        operation: 'upload-lecture-file',
        consoleMessage: 'Upload error:',
        context: { fileName: file?.name, fileType: file?.type, fileSize: file?.size, uploadType: type }
      });
      const errorMessage = error.message || 'Upload failed. Please try again.';
      setUploadError(errorMessage);
      return null;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Remove a file from S3 storage
   */
  const removeFile = useCallback(async (key, accessLevel = 'protected') => {
    if (!key) return;
    
    try {
      await remove({ 
        key, 
        options: { accessLevel } 
      });
      return true;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'remove-file',
        consoleMessage: 'Error removing file:',
        context: { key, accessLevel }
      });
      throw error;
    }
  }, []);

  /**
   * Get a download URL for a file
   */
  const getFileUrl = useCallback(async (key, accessLevel = 'protected') => {
    if (!key) return null;
    
    try {
      const getUrlResult = await getUrl({
        key,
        options: { accessLevel }
      });
      return getUrlResult.url;
    } catch (error) {
      captureError(error, {
        feature: 'lecture-management',
        operation: 'get-file-url',
        consoleMessage: 'Error getting file URL:',
        context: { key, accessLevel }
      });
      throw error;
    }
  }, []);

  /**
   * Convert a file to base64 string
   */
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Get the appropriate icon for a file type
   */
  const getFileIcon = useCallback((fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'ppt':
      case 'pptx':
        return 'file-powerpoint';
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      default:
        return 'file-alt';
    }
  }, []);

  /**
   * Get the appropriate color for a file type
   */
  const getFileColor = useCallback((fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'ppt':
      case 'pptx':
        return 'text-warning';
      case 'pdf':
        return 'text-danger';
      case 'doc':
      case 'docx':
        return 'text-primary';
      default:
        return 'text-secondary';
    }
  }, []);

  /**
   * Reset upload state
   */
  const resetUploadState = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError(null);
  }, []);

  return {
    uploadLectureImage,
    uploadLectureFile,
    removeFile,
    getFileUrl,
    getFileIcon,
    getFileColor,
    resetUploadState,
    isUploading,
    uploadProgress,
    uploadError
  };
};